import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Tooltip } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Column, DataTable } from '../../../components/DataTable';
import { ProcessingType, PR_PROCESSING_TYPES } from '../../../types';
import { formatDate } from '../../../utils';

export interface ModEligibilityRow {
  modCoverageId: number;
  tagName: string;
  cameraLabel: string | null;
  hasImage: boolean;
  processingType: ProcessingType | null;
  isComplianceEnabled: boolean;
  complianceEligibilitySource: string | null;
  complianceEligibilityUpdatedByEmail: string | null;
  complianceEligibilityUpdatedAt: string | null;
}

export const isProcessingTypeInconsistent = (row: {
  processingType: ProcessingType | null;
  isComplianceEnabled: boolean;
}): boolean =>
  row.processingType !== null &&
  PR_PROCESSING_TYPES.includes(row.processingType) &&
  !row.isComplianceEnabled;

interface RowWithSelection extends ModEligibilityRow {
  _isSelected: boolean;
}

// Stable reference — DataTable's `initialFilters` effect re-fires every render if the
// caller passes a new array each time (its default `= []` does so), causing a render loop.
const EMPTY_FILTERS: never[] = [];

interface ModEligibilityTableProps {
  rows: ModEligibilityRow[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  isSearchActive: boolean;
}

const renderDash = (value: string | null | undefined) =>
  value ? <span>{value}</span> : <span className="text-gray-400">—</span>;

export const ModEligibilityTable: React.FC<ModEligibilityTableProps> = ({
  rows,
  selectedIds,
  onSelectionChange,
  isSearchActive,
}) => {
  // DataTable stores `columns` in internal useState (captured once on mount), so render
  // closures can't see fresh React state. Route handlers through refs so DataTable's
  // single captured column list always calls the latest handler.
  const rowsRef = useRef(rows);
  const selectedIdsRef = useRef(selectedIds);
  const onSelectionChangeRef = useRef(onSelectionChange);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  const allFilteredSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r.modCoverageId));
  const someFilteredSelected =
    !allFilteredSelected && rows.some((r) => selectedIds.has(r.modCoverageId));

  const masterRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (masterRef.current) {
      masterRef.current.indeterminate = someFilteredSelected;
      masterRef.current.checked = allFilteredSelected;
    }
  }, [someFilteredSelected, allFilteredSelected]);

  const toggleAllFiltered = () => {
    const currentRows = rowsRef.current;
    const current = selectedIdsRef.current;
    const everySelected =
      currentRows.length > 0 && currentRows.every((r) => current.has(r.modCoverageId));
    const next = new Set(current);
    if (everySelected) {
      currentRows.forEach((r) => next.delete(r.modCoverageId));
    } else {
      currentRows.forEach((r) => next.add(r.modCoverageId));
    }
    onSelectionChangeRef.current(next);
  };

  const toggleOne = (id: number) => {
    const current = selectedIdsRef.current;
    const next = new Set(current);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChangeRef.current(next);
  };

  // DataTable's internal sort is unreliable on toggle; track sort state here and pre-sort
  // data ourselves. onSortChange fires on every header click.
  const [orderBy, setOrderBy] = useState<keyof RowWithSelection>('tagName');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');

  // Bake selection into each row so DataTable sees a new `data` reference and re-renders
  // rows when selection changes (its internal column state is captured once and won't
  // update, so data-driven re-renders are our only handle).
  const rowsWithSelection = useMemo<RowWithSelection[]>(() => {
    const base = rows.map((r) => ({ ...r, _isSelected: selectedIds.has(r.modCoverageId) }));
    const dir = orderDirection === 'asc' ? 1 : -1;
    base.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return (av < bv ? -1 : 1) * dir;
    });
    return base;
  }, [rows, selectedIds, orderBy, orderDirection]);

  const columns = useMemo<Column<RowWithSelection>[]>(
    () => [
      {
        field: '_selected' as keyof RowWithSelection,
        title: (
          <label
            className="flex items-center justify-center -mx-4 -my-3 px-4 py-3 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              ref={masterRef}
              type="checkbox"
              onChange={toggleAllFiltered}
              className="w-4 h-4 cursor-pointer"
              aria-label="Select all filtered mods"
            />
          </label>
        ),
        sortable: false,
        width: '48px',
        render: (_value, row) => (
          <label
            className="flex items-center justify-center -mx-4 -my-3 px-4 py-3 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={row._isSelected}
              onChange={() => toggleOne(row.modCoverageId)}
              className="w-4 h-4 cursor-pointer"
              aria-label={`Select ${row.tagName}`}
            />
          </label>
        ),
      },
      { field: 'modCoverageId', title: 'Mod coverage id', sortable: true },
      {
        field: 'tagName',
        title: 'Mod',
        sortable: true,
        render: (value: string, row) => (
          <span className="inline-flex items-center gap-1">
            <span>{value}</span>
            {isProcessingTypeInconsistent(row) && (
              <Tooltip
                title={`Processing type '${row.processingType}' requires PC & PR enabled, but PC & PR is disabled for this mod.`}
                arrow
              >
                <WarningAmberIcon className="text-amber-500" fontSize="small" />
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        field: 'processingType',
        title: 'Processing type',
        sortable: true,
        render: (value: ProcessingType | null) => renderDash(value),
      },
      {
        field: 'cameraLabel',
        title: 'Camera',
        sortable: true,
        render: (value: string | null) => renderDash(value),
      },
      {
        field: 'hasImage',
        title: 'Image',
        sortable: true,
        render: (value: boolean) =>
          value ? (
            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
              Yes
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">No</span>
          ),
      },
      {
        field: 'isComplianceEnabled',
        title: 'PC & PR enabled',
        sortable: true,
        render: (value: boolean) =>
          value ? (
            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
              Yes
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">No</span>
          ),
      },
      {
        field: 'complianceEligibilitySource',
        title: 'Source',
        sortable: true,
        render: (value: string) => renderDash(value),
      },
      {
        field: 'complianceEligibilityUpdatedByEmail',
        title: 'Updated by',
        sortable: true,
        render: (value: string) => renderDash(value),
      },
      {
        field: 'complianceEligibilityUpdatedAt',
        title: 'Updated at',
        sortable: true,
        render: (value: string) =>
          value ? formatDate(value) : <span className="text-gray-400">—</span>,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const enabledCount = rows.reduce((n, r) => (r.isComplianceEnabled ? n + 1 : n), 0);

  return (
    <DataTable<RowWithSelection>
      key={isSearchActive ? 'search' : 'paged'}
      data={rowsWithSelection}
      columns={columns}
      idKey="modCoverageId"
      searchable={false}
      filterable={false}
      title={`PC & PR enabled: ${enabledCount} / ${rows.length} mods${
        rows.length > 0 ? ` (${Math.round((enabledCount / rows.length) * 100)}%)` : ''
      }`}
      pageSize={isSearchActive ? 5000 : 250}
      pageSizeOptions={[100, 250, 1000, 5000]}
      tableKey="mod-eligibility"
      defaultHiddenColumns={['modCoverageId']}
      initialFilters={EMPTY_FILTERS}
      initialOrderBy={orderBy}
      initialOrderDirection={orderDirection}
      onSortChange={(newOrderBy, newDirection) => {
        if (newOrderBy) setOrderBy(newOrderBy);
        setOrderDirection(newDirection);
      }}
    />
  );
};
